import { IReportDefinition } from '@common/interfaces/report'
import { IApi } from '../../api'
import { createRequestTypes } from '../../helpers/helpers'
import { createAction } from '../types'

export const prefix = 'reports'

export interface IReportDraft extends IReportDefinition {
  name: string
  description: string | null
  // A new report's conversation with Arc, which carries on from its page once it's saved.
  arcChannelId?: string
  // Refuses the save if the report changed after this time.
  expectedUpdatedAt?: string
}

export const [LIST_REPORTS_REQUEST, LIST_REPORTS_SUCCESS, LIST_REPORTS_FAILURE] =
  createRequestTypes(prefix, 'LIST_REPORTS')

export const listReports = createAction(() => ({
  type: [LIST_REPORTS_REQUEST, LIST_REPORTS_SUCCESS, LIST_REPORTS_FAILURE],
  promise: (api: IApi) => api.reports.list(),
}))

export const [GET_REPORT_REQUEST, GET_REPORT_SUCCESS, GET_REPORT_FAILURE] = createRequestTypes(
  prefix,
  'GET_REPORT',
)

export const getReport = createAction((id: string) => ({
  type: [GET_REPORT_REQUEST, GET_REPORT_SUCCESS, GET_REPORT_FAILURE],
  promise: (api: IApi) => api.reports.show(id),
}))

export const [CREATE_REPORT_REQUEST, CREATE_REPORT_SUCCESS, CREATE_REPORT_FAILURE] =
  createRequestTypes(prefix, 'CREATE_REPORT')

export const createReport = createAction((data: IReportDraft) => ({
  type: [CREATE_REPORT_REQUEST, CREATE_REPORT_SUCCESS, CREATE_REPORT_FAILURE],
  promise: (api: IApi) => api.reports.create(data),
}))

export const [UPDATE_REPORT_REQUEST, UPDATE_REPORT_SUCCESS, UPDATE_REPORT_FAILURE] =
  createRequestTypes(prefix, 'UPDATE_REPORT')

export const updateReport = createAction((id: string, data: IReportDraft) => ({
  type: [UPDATE_REPORT_REQUEST, UPDATE_REPORT_SUCCESS, UPDATE_REPORT_FAILURE],
  promise: (api: IApi) => api.reports.update(id, data),
}))

export const [SHARE_REPORT_REQUEST, SHARE_REPORT_SUCCESS, SHARE_REPORT_FAILURE] =
  createRequestTypes(prefix, 'SHARE_REPORT')

export const shareReport = createAction(
  (id: string, data: { userIds: string[]; teamIds: string[] }) => ({
    type: [SHARE_REPORT_REQUEST, SHARE_REPORT_SUCCESS, SHARE_REPORT_FAILURE],
    promise: (api: IApi) => api.reports.share(id, data),
  }),
)

export const [DELETE_REPORT_REQUEST, DELETE_REPORT_SUCCESS, DELETE_REPORT_FAILURE] =
  createRequestTypes(prefix, 'DELETE_REPORT')

export const deleteReport = createAction((id: string) => ({
  type: [DELETE_REPORT_REQUEST, DELETE_REPORT_SUCCESS, DELETE_REPORT_FAILURE],
  promise: (api: IApi) => api.reports.delete(id),
}))

export const [RUN_REPORT_REQUEST, RUN_REPORT_SUCCESS, RUN_REPORT_FAILURE] = createRequestTypes(
  prefix,
  'RUN_REPORT',
)

// `loadMore` appends the page to the rows on screen; it stays on the client.
export const runReport = createAction(
  (params: IReportDefinition & { page: number; perPage: number }, loadMore = false) => ({
    type: [RUN_REPORT_REQUEST, RUN_REPORT_SUCCESS, RUN_REPORT_FAILURE],
    promise: (api: IApi) => api.reports.run(params),
    params: { ...params, loadMore },
  }),
)

export const [EXPORT_REPORT_REQUEST, EXPORT_REPORT_SUCCESS, EXPORT_REPORT_FAILURE] =
  createRequestTypes(prefix, 'EXPORT_REPORT')

export const exportReport = createAction((params: IReportDefinition & { name?: string }) => ({
  type: [EXPORT_REPORT_REQUEST, EXPORT_REPORT_SUCCESS, EXPORT_REPORT_FAILURE],
  promise: (api: IApi) => api.reports.export(params),
}))

export const [
  OPEN_REPORT_CONVERSATION_REQUEST,
  OPEN_REPORT_CONVERSATION_SUCCESS,
  OPEN_REPORT_CONVERSATION_FAILURE,
] = createRequestTypes(prefix, 'OPEN_REPORT_CONVERSATION')

// The report's AIM conversation with Arc; a new report (no id) gets one of its own.
export const openReportConversation = createAction((reportId: string | null) => ({
  type: [
    OPEN_REPORT_CONVERSATION_REQUEST,
    OPEN_REPORT_CONVERSATION_SUCCESS,
    OPEN_REPORT_CONVERSATION_FAILURE,
  ],
  promise: (api: IApi) => api.reports.openConversation(reportId ? { reportId } : {}),
}))
